import produce from 'immer';
import { cloneDeep, isEqual } from 'lodash';
import * as React from 'react';
import { Reducer, useEffect, useReducer } from 'react';
import { Button, ButtonMode, ButtonSize, ButtonVariant } from '../Components/Button';
import { ButtonGroup } from '../Components/ButtonGroup';
import { StyledSelect } from '../Components/StyledSelect';
import { MedicationListStatus, MedicationListType } from '../Contracts/MedicationListsSearchFilter.Request';
import { useDebounce } from '../Hooks/useDebounce';
import { ApplicationStorageKeys, loadFromLocalStorage, saveToLocalStorage } from '../Shared/LocalStorage';
import { Nullable } from '../Shared/Types';
import { divider } from '../Styles/Colours';
import styles from './MedicationListFilter.module.scss';

export type MedListFilterProps = {
    state: MedListFilterState;
    filtered: boolean;
    updateField: (field: keyof MedListFilterState) => (value: unknown) => void;
    clearField: (field: keyof MedListFilterState, clearValue: any) => () => void;
    updateLimit: (limit: number) => () => void;
    clearFilter: () => void;
    refresh: () => void;
};

type SearchValues<T> = {
    name: string;
    value: T;
};

type SearchMedicationListType = SearchValues<MedicationListType>;
type SearchMedicationListStatus = SearchValues<'All' | MedicationListStatus>;

export type MedListFilterState = {
    types: SearchMedicationListType[];
    status: Nullable<SearchMedicationListStatus>;
    limit: number;
};

const allStatus: SearchMedicationListStatus = { value: 'All', name: 'All' };

const clearFilterDefaults: MedListFilterState = {
    types: [],
    status: allStatus,
    limit: 50
};

export const isMedListFiltered = (filter: MedListFilterState): boolean => {
    return !isEqual(filter, clearFilterDefaults);
};

const medListStatuses: SearchMedicationListStatus[] = [
    allStatus,
    { value: MedicationListStatus.Draft, name: 'Draft' },
    { value: MedicationListStatus.Published, name: 'Published' }
];
const medListTypes: SearchMedicationListType[] = [
    { value: MedicationListType.ScopeOfPracticeList, name: 'Scope of Practice' },
    { value: MedicationListType.Formulary, name: 'Formulary' },
    { value: MedicationListType.DefaultList, name: 'Default' }
];

type FilterActions =
    | { type: 'UpdateField'; field: keyof MedListFilterState; value: any }
    | { type: 'UpdateLimit'; value: number }
    | { type: 'Clear' };

const filterReducer = produce((state: MedListFilterState, action: FilterActions) => {
    switch (action.type) {
        case 'UpdateField':
            if (action.field === 'types' && !action.value) state[action.field] = [];
            else (state[action.field] as any) = action.value;
            break;
        case 'UpdateLimit':
            state.limit = action.value;
            break;
        case 'Clear':
            return cloneDeep(clearFilterDefaults);
    }
});

export function useMedicationListFilter(
    filterUpdatedCallback: (filter: MedListFilterState) => Promise<void>
): MedListFilterProps {
    const [state, dispatch] = useReducer<Reducer<MedListFilterState, FilterActions>>(
        filterReducer,
        loadFromLocalStorage<MedListFilterState>(
            ApplicationStorageKeys.MedicationListFilters,
            cloneDeep(clearFilterDefaults)
        )
    );

    useEffect(() => {
        saveToLocalStorage(ApplicationStorageKeys.MedicationListFilters, state);
    }, [state]);

    const debouncedState: MedListFilterState = useDebounce(state);

    useEffect(() => {
        filterUpdatedCallback(debouncedState);
    }, [debouncedState]);

    const updateField = (field: keyof MedListFilterState) => (value: unknown) => {
        dispatch({ type: 'UpdateField', field, value });
    };

    const updateLimit = (value: number) => () => {
        dispatch({ type: 'UpdateLimit', value });
    };

    const clearField = (field: keyof MedListFilterState, clearValue: any) => () => {
        dispatch({ type: 'UpdateField', field, value: clearValue });
    };

    const clearFilter = () => {
        dispatch({ type: 'Clear' });
    };

    const refresh = () => {
        filterUpdatedCallback(debouncedState);
    };

    const filtered = useDebounce(!isEqual(clearFilterDefaults, debouncedState), 100);

    return {
        state,
        filtered,
        updateField,
        clearField,
        updateLimit,
        refresh,
        clearFilter
    };
}

export function MedicationListFilter(props: MedListFilterProps) {
    return (
        <div className={styles.containFilters}>
            <div className={styles.controls}>
                <div className={styles.filterPadding}>
                    <div className={styles.filterRow}>
                        <StyledSelect<SearchMedicationListType>
                            testId='type'
                            label={'Type'}
                            itemToString={it => it.name}
                            itemToMenuKey={it => it.value}
                            onSelection={props.updateField('types')}
                            suggestions={medListTypes}
                            value={props.state.types}
                            maxResults={0}
                            isClearable
                            isMulti
                        />
                    </div>

                    <div className={styles.filterRow}>
                        <StyledSelect<SearchMedicationListStatus>
                            testId='status'
                            label={'Status'}
                            itemToString={it => it.name}
                            itemToMenuKey={it => it.value}
                            onSelection={props.updateField('status')}
                            suggestions={medListStatuses}
                            value={props.state.status}
                            maxResults={0}
                            isClearable={false}
                        />
                    </div>
                </div>

                <div className={styles.sectionTitle}>Show results</div>
                <div className={styles.metricPeriodPicker}>
                    <ButtonGroup borderColour={divider}>
                        <Button
                            size={ButtonSize.small}
                            width='25%'
                            mode={props.state.limit === 25 ? ButtonMode.solid : ButtonMode.border}
                            variant={props.state.limit === 25 ? ButtonVariant.primary : ButtonVariant.light}
                            onClick={props.updateLimit(25)}
                            testId={'filter-records-25'}
                        >
                            25
                        </Button>
                        <Button
                            size={ButtonSize.small}
                            width='25%'
                            mode={props.state.limit === 50 ? ButtonMode.solid : ButtonMode.border}
                            variant={props.state.limit === 50 ? ButtonVariant.primary : ButtonVariant.light}
                            onClick={props.updateLimit(50)}
                            testId={'filter-records-50'}
                        >
                            50
                        </Button>
                        <Button
                            size={ButtonSize.small}
                            width='25%'
                            mode={props.state.limit === 100 ? ButtonMode.solid : ButtonMode.border}
                            variant={props.state.limit === 100 ? ButtonVariant.primary : ButtonVariant.light}
                            onClick={props.updateLimit(100)}
                            testId={'filter-records-100'}
                        >
                            100
                        </Button>
                        <Button
                            size={ButtonSize.small}
                            width='25%'
                            mode={props.state.limit === 0 ? ButtonMode.solid : ButtonMode.border}
                            variant={props.state.limit === 0 ? ButtonVariant.primary : ButtonVariant.light}
                            onClick={props.updateLimit(0)}
                            testId={'filter-records-all'}
                        >
                            All
                        </Button>
                    </ButtonGroup>
                </div>
            </div>
            <div className={styles.clearButtonContainer}>
                <Button
                    onClick={props.clearFilter}
                    width='100%'
                    size={ButtonSize.small}
                    variant={ButtonVariant.light}
                    testId='clear-filter'
                    color='primary'
                >
                    Clear all filters
                </Button>
            </div>
        </div>
    );
}
